import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import {
  RefreshCcw, Plus, Trash2, Database, Globe2, Flag, CheckCircle2, Clock, Search,
  TrendingUp, BarChart3, Sparkles, Calculator, Eye, MoreVertical, Loader2, Zap,
  ChevronDown,
} from "lucide-react";
import { apiGet, apiPost, formatNumber, formatDateTime } from "@/lib/api";
import { toast } from "sonner";

const StatTile = ({ icon: Icon, label, value, hint, testId, accent = "default" }) => {
  const accentClass = {
    default: "bg-primary/10 text-primary",
    growth: "bg-violet-500/10 text-violet-600 dark:text-violet-400",
    value: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
    warn: "bg-amber-500/10 text-amber-700 dark:text-amber-400",
  }[accent];
  return (
    <Card data-testid={testId}>
      <CardContent className="pt-6">
        <div className="flex items-start gap-3">
          <div className={`rounded-lg p-2.5 ${accentClass}`}><Icon className="h-5 w-5" /></div>
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
            <div className="text-2xl font-semibold tracking-tight truncate" data-testid={`${testId}-value`}>{value ?? "—"}</div>
            {hint ? <div className="text-xs text-muted-foreground mt-0.5">{hint}</div> : null}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const SIGNAL_CLASS = {
  STRONG_BUY: "bg-emerald-600 text-white hover:bg-emerald-600",
  BUY: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30",
  WATCH: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/30",
  HOLD: "bg-muted text-muted-foreground",
  AVOID: "bg-destructive/15 text-destructive border border-destructive/30",
};

const SignalBadge = ({ signal }) => {
  if (!signal) return <span className="text-xs text-muted-foreground">—</span>;
  return <Badge className={`${SIGNAL_CLASS[signal] || ""} text-xs font-medium`}>{signal.replace("_", " ")}</Badge>;
};

const ValuationBadge = ({ status }) => {
  if (!status) return null;
  const meta = {
    "Deep Value":   { cls: "bg-[#14532d] text-white border-[#14532d]",  icon: "💎" },
    "Undervalued":  { cls: "bg-[#16a34a] text-white border-[#16a34a]",  icon: "📉" },
    "Fair Value":   { cls: "bg-[#6b7280] text-white border-[#6b7280]",  icon: "⚖" },
    "Overvalued":   { cls: "bg-[#ea580c] text-white border-[#ea580c]",  icon: "📈" },
    "Overpriced":   { cls: "bg-[#dc2626] text-white border-[#dc2626]",  icon: "🔴" },
    "Unknown":      { cls: "bg-muted text-muted-foreground border-border",  icon: "—" },
  }[status];
  if (!meta) return <Badge variant="outline" className="text-xs">{status}</Badge>;
  return (
    <Badge variant="outline" className={`${meta.cls} text-xs font-medium inline-flex items-center gap-1 px-2.5 py-0.5`}>
      <span aria-hidden="true" className="leading-none">{meta.icon}</span>
      {status}
    </Badge>
  );
};

// ----- Score breakdown dialog -----------------------------------------------

const fmtCurrency = (v) => (v == null || isNaN(v) ? "—" : `$${Number(v).toFixed(2)}`);
const fmtPct = (v, d = 2) => (v == null || isNaN(v) ? "—" : `${Number(v).toFixed(d)}%`);
const fmtNumber = (v, d = 2) => (v == null || isNaN(v) ? "—" : Number(v).toFixed(d));

const LayerCard = ({ title, layer }) => {
  const pct = layer?.max_score ? (layer.score / layer.max_score) * 100 : 0;
  return (
    <div className="rounded-lg border p-3.5">
      <div className="flex items-baseline justify-between mb-1.5">
        <div className="text-sm font-semibold">{title}</div>
        <div className="text-xs text-muted-foreground tabular-nums">
          <span className="font-semibold text-foreground">{layer?.score?.toFixed(2) ?? "—"}</span>
          <span> / {layer?.max_score ?? "—"}</span>
        </div>
      </div>
      <Progress value={Math.max(0, Math.min(100, pct))} className="h-1.5" />
      {layer?.components ? (
        <ul className="mt-2.5 space-y-1 text-xs text-muted-foreground">
          {Object.entries(layer.components).map(([k, c]) => {
            if (c == null) return null;
            // "trend" / "ma50" / "ma200" / "rsi" — special-cased so they render cleanly.
            if (k === "ma50" || k === "ma200") {
              return <li key={k} className="flex justify-between"><span>{k.toUpperCase()}</span><span className="tabular-nums">{fmtCurrency(c)}</span></li>;
            }
            if (typeof c !== "object") return null;
            const v = c.value != null ? c.value : c.mean;
            const sc = c.score;
            return (
              <li key={k} className="flex justify-between gap-2">
                <span className="capitalize truncate">{k.replace(/_/g, " ")}</span>
                <span className="tabular-nums">
                  {typeof v === "number" ? fmtNumber(v) : (v ?? "—")}
                  {sc != null ? <span className="text-foreground/60 ml-2">{Math.round(sc)}/100</span> : null}
                </span>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
};

const ScoreBreakdownDialog = ({ open, onOpenChange, symbol }) => {
  const [score, setScore] = useState(null);
  const [fund, setFund] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !symbol) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [s, f] = await Promise.all([
          apiGet(`/us-stocks/${encodeURIComponent(symbol)}/composite-score`),
          apiGet(`/us-stocks/${encodeURIComponent(symbol)}/fundamentals`).catch(() => null),
        ]);
        if (!cancelled) { setScore(s); setFund(f); }
      } catch (e) {
        if (!cancelled) toast.error(`Failed to load score for ${symbol}`, { description: e?.response?.data?.detail || e.message });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, symbol]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="us-score-breakdown-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-baseline gap-3">
            <span className="font-mono">{symbol}</span>
            {score?.company_name ? <span className="text-sm font-normal text-muted-foreground">{score.company_name}</span> : null}
          </DialogTitle>
          <DialogDescription>
            6-layer composite score · {score?.growth_or_value ? <span className="font-medium uppercase">{score.growth_or_value}</span> : "—"}
            {score?.classification_reason ? <span className="text-xs"> ({score.classification_reason})</span> : null}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="space-y-3"><Skeleton className="h-24 w-full" /><Skeleton className="h-32 w-full" /></div>
        ) : score ? (
          <div className="space-y-4">
            {/* Headline */}
            <div className="rounded-xl border-2 p-4" style={{ borderColor: "hsl(var(--border))" }}>
              <div className="flex flex-wrap items-center gap-3 justify-between">
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">Composite score</div>
                  <div className="text-3xl font-bold tabular-nums">{score.composite_score?.toFixed(1)} <span className="text-base font-normal text-muted-foreground">/ 100</span></div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <SignalBadge signal={score.signal} />
                  <ValuationBadge status={score.valuation_status} />
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 text-sm">
                <div><div className="text-xs text-muted-foreground">Latest close</div><div className="font-semibold tabular-nums">{fmtCurrency(score.latest_close)}</div></div>
                <div><div className="text-xs text-muted-foreground">Base target</div><div className="font-semibold tabular-nums">{fmtCurrency(score.base_target_price)}</div></div>
                <div><div className="text-xs text-muted-foreground">Upside</div><div className={`font-semibold tabular-nums ${score.upside_pct > 0 ? "text-emerald-600" : score.upside_pct < 0 ? "text-destructive" : ""}`}>{score.upside_pct != null ? fmtPct(score.upside_pct, 1) : "—"}</div></div>
                <div><div className="text-xs text-muted-foreground">Sector</div><div className="font-semibold truncate">{score.sector || "—"}</div></div>
              </div>
            </div>

            {/* Method table */}
            <div className="rounded-lg border">
              <div className="px-3 py-2 border-b bg-muted/30 text-xs font-medium flex items-center justify-between">
                <span>Valuation methods ({score.company_type ? score.company_type.replace(/_/g, " ") : "—"})</span>
                {score.company_type_reason ? <span className="text-muted-foreground font-normal">{score.company_type_reason}</span> : null}
              </div>
              <div className="divide-y">
                {Object.entries(score.valuation_methods || {}).map(([key, m]) => {
                  const isUsed = m.used_weight != null && m.value != null;
                  const hasValue = m.value != null;
                  return (
                    <div key={key} className="px-3 py-2 flex items-center gap-3 text-xs">
                      <div className="w-44 flex-shrink-0 font-medium">{m.label}</div>
                      <div className={`w-24 tabular-nums ${hasValue ? "" : "text-muted-foreground"}`}>
                        {hasValue ? fmtCurrency(m.value) : "N/A"}
                      </div>
                      <div className="flex-1">
                        {isUsed ? (
                          <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30 text-[10px]">
                            Used ({Math.round(m.used_weight * 100)}%)
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground italic">
                            {m.applicable ? "N/A — data gap" : "⚫ Not applicable"}
                            {m.na_reason ? <span className="text-[10px] ml-1.5 text-muted-foreground/80">— {m.na_reason}</span> : null}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
                {/* Base case totals */}
                <div className="px-3 py-2.5 flex items-center gap-3 bg-muted/40 text-sm font-semibold">
                  <div className="w-44 flex-shrink-0">Base case target</div>
                  <div className="w-24 tabular-nums">{fmtCurrency(score.base_target_price)}</div>
                  <div className="flex-1 text-xs font-normal text-muted-foreground">
                    Weighted blend across {Object.values(score.valuation_methods || {}).filter((m) => m.used_weight && m.value).length} applied methods
                    {score.total_weight_applied && score.total_weight_applied < 0.99 ? ` (coverage: ${Math.round(score.total_weight_applied * 100)}%)` : ""}
                  </div>
                </div>
              </div>
            </div>

            {/* Layers */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <LayerCard title="L1 · Valuation" layer={score.layer1_valuation} />
              <LayerCard title="L2 · Growth" layer={score.layer2_growth} />
              <LayerCard title="L3 · Quality" layer={score.layer3_quality} />
              <LayerCard title="L4 · Intrinsic value" layer={score.layer4_intrinsic} />
              <LayerCard title="L5 · Forward intelligence" layer={score.layer5_intelligence} />
              <LayerCard title="L6 · Technical" layer={score.layer6_technical} />
            </div>

            {/* Raw fundamentals quick view */}
            {fund ? (
              <details className="rounded-md border p-3 text-xs">
                <summary className="cursor-pointer font-semibold">Raw fundamentals (yfinance)</summary>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 text-muted-foreground">
                  <div>PE: <span className="text-foreground tabular-nums">{fmtNumber(fund.pe_ratio)}</span></div>
                  <div>PB: <span className="text-foreground tabular-nums">{fmtNumber(fund.pb_ratio)}</span></div>
                  <div>P/S: <span className="text-foreground tabular-nums">{fmtNumber(fund.ps_ratio)}</span></div>
                  <div>PEG: <span className="text-foreground tabular-nums">{fmtNumber(fund.peg_ratio)}</span></div>
                  <div>Yield: <span className="text-foreground tabular-nums">{fund.dividend_yield != null ? fmtPct(fund.dividend_yield * 100) : "—"}</span></div>
                  <div>ROE: <span className="text-foreground tabular-nums">{fund.roe != null ? fmtPct(fund.roe * 100) : "—"}</span></div>
                  <div>D/E: <span className="text-foreground tabular-nums">{fmtNumber(fund.debt_to_equity)}</span></div>
                  <div>Margin: <span className="text-foreground tabular-nums">{fund.profit_margin != null ? fmtPct(fund.profit_margin * 100) : "—"}</span></div>
                  <div>Rev CAGR: <span className="text-foreground tabular-nums">{fund.revenue_cagr_5y != null ? fmtPct(fund.revenue_cagr_5y * 100) : "—"}</span></div>
                  <div>EPS CAGR: <span className="text-foreground tabular-nums">{fund.eps_cagr_5y != null ? fmtPct(fund.eps_cagr_5y * 100) : "—"}</span></div>
                  <div>FCF: <span className="text-foreground tabular-nums">{fund.free_cashflow_ttm != null ? `$${(fund.free_cashflow_ttm / 1e9).toFixed(1)}B` : "—"}</span></div>
                  <div>Mkt Cap: <span className="text-foreground tabular-nums">{fund.market_cap != null ? `$${(fund.market_cap / 1e9).toFixed(1)}B` : "—"}</span></div>
                  <div>Analyst: <span className="text-foreground">{fund.recommendation_key || "—"} ({fund.num_analysts || 0})</span></div>
                  <div>Target: <span className="text-foreground tabular-nums">{fmtCurrency(fund.target_mean_price)}</span></div>
                </div>
              </details>
            ) : null}
          </div>
        ) : (
          <div className="p-8 text-center text-sm text-muted-foreground">
            No score stored for {symbol}. Refresh fundamentals + recompute scores first.
          </div>
        )}

        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ----- Job Progress Card (Phase 3) ------------------------------------------

const JobProgressCard = ({ title, progress, accentColor = "primary" }) => {
  if (!progress) return null;
  // Only render when there's been at least one start or completion. Skip
  // pure idle state so the page doesn't show empty progress widgets.
  if (progress.status === "idle" && !progress.started_at) return null;

  const pct = Number(progress.pct || 0);
  const isRunning = progress.status === "running";
  const isComplete = progress.status === "completed";
  const isFailed = progress.status === "failed";
  const eta = progress.eta_seconds;

  const formatEta = (s) => {
    if (s == null) return "—";
    if (s < 60) return `${Math.round(s)}s`;
    const m = Math.floor(s / 60);
    const sec = Math.round(s % 60);
    return `${m}m ${sec}s`;
  };
  const formatElapsed = (s) => {
    if (s == null) return "—";
    if (s < 60) return `${Math.round(s)}s`;
    return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
  };

  const statusBadge = isRunning
    ? <Badge variant="outline" className="bg-primary/15 text-primary border-primary/30 animate-pulse">Running</Badge>
    : isComplete
      ? <Badge variant="outline" className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30">Completed</Badge>
      : isFailed
        ? <Badge variant="outline" className="bg-destructive/15 text-destructive border-destructive/30">Failed</Badge>
        : <Badge variant="outline">Idle</Badge>;

  return (
    <Card data-testid={`us-job-progress-${progress.job}`}>
      <CardContent className="pt-4 pb-4 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className={`inline-block h-2 w-2 rounded-full ${
              isRunning ? "bg-primary animate-pulse" :
              isComplete ? "bg-emerald-500" :
              isFailed ? "bg-destructive" : "bg-muted-foreground"
            }`} />
            <div className="text-sm font-medium">{title}</div>
            {statusBadge}
          </div>
          <div className="text-xs text-muted-foreground tabular-nums">
            {progress.done} / {progress.total} ({pct.toFixed(1)}%)
          </div>
        </div>

        <Progress value={pct} className="h-2" />

        <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-muted-foreground">
          {isRunning ? (
            <span>Est. remaining: <span className="text-foreground font-medium">{formatEta(eta)}</span></span>
          ) : null}
          <span>Elapsed: <span className="text-foreground font-medium">{formatElapsed(progress.elapsed_seconds)}</span></span>
          <span>Succeeded: <span className="text-emerald-600 dark:text-emerald-400 font-medium">{progress.succeeded}</span></span>
          <span>Failed: <span className={`font-medium ${progress.failed > 0 ? "text-destructive" : "text-foreground"}`}>{progress.failed}</span></span>
        </div>

        {progress.message ? (
          <div className="text-xs italic text-muted-foreground truncate" data-testid={`us-job-progress-message-${progress.job}`}>
            {progress.message}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};

// ----- Main page ------------------------------------------------------------

const AdminUsUniversePage = () => {
  const [stats, setStats] = useState(null);
  const [scoringStats, setScoringStats] = useState(null);
  const [items, setItems] = useState([]);
  const [scoresBySymbol, setScoresBySymbol] = useState({});
  const [loadingList, setLoadingList] = useState(true);
  const [loadingAction, setLoadingAction] = useState(false);
  const [search, setSearch] = useState("");
  const [membershipFilter, setMembershipFilter] = useState("all");
  const [activeOnly, setActiveOnly] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addSymbol, setAddSymbol] = useState("");
  const [auditLog, setAuditLog] = useState([]);
  const [scoreDialog, setScoreDialog] = useState({ open: false, symbol: null });
  // Phase 3 — live progress for the two long-running admin jobs.
  // `null` = no recent run; otherwise the latest status payload from
  // `/admin/us-fundamentals/refresh-status` or `/admin/us-scores/recompute-status`.
  const [fundProgress, setFundProgress] = useState(null);
  const [scoreProgress, setScoreProgress] = useState(null);

  const loadAll = useCallback(async () => {
    setLoadingList(true);
    try {
      const [s, sc, list, log] = await Promise.all([
        apiGet("/admin/us-universe/stats"),
        apiGet("/admin/us-scores/stats").catch(() => null),
        apiGet(`/us-universe?only_active=${activeOnly}&limit=600${
          membershipFilter !== "all" ? `&membership=${membershipFilter}` : ""
        }`),
        apiGet("/admin/us-universe/log?limit=20"),
      ]);
      setStats(s);
      setScoringStats(sc);
      setItems(list?.items || []);
      setAuditLog(log?.items || []);

      // Hydrate scores in parallel — only when count is reasonable.
      if (sc?.scored_count > 0) {
        // Fetch in batches of 100 to avoid hammering. We just read the
        // composite_scores collection via a single bulk endpoint? We don't
        // have one — fall back to a lightweight approach: skip per-row
        // hydration for now and rely on the dialog to fetch on click.
      }
    } catch (e) {
      toast.error("Failed to load US universe", { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoadingList(false);
    }
  }, [activeOnly, membershipFilter]);

  // Bulk-hydrate composite scores for all rows in ONE round-trip via the
  // existing screener endpoint `/api/us-stocks?limit=600`. Previously this
  // was an N+1 loop capped at the first 100 tickers, which left rows
  // 101-509 permanently showing "—" for Score/Signal even though the DB
  // had scores for every ticker. Also exposed as a callable so handlers
  // (e.g. onRecomputeScores) can re-run it after a job finishes.
  const hydrateScores = useCallback(async () => {
    try {
      const res = await apiGet("/us-stocks?limit=600&sort=symbol_asc");
      const rows = res?.items || [];
      const map = {};
      for (const it of rows) {
        if (!it?.symbol) continue;
        map[it.symbol] = {
          composite_score: it.composite_score,
          signal: it.signal,
          growth_or_value: it.growth_or_value,
          company_type: it.company_type,
          valuation_status: it.valuation_status,
        };
      }
      setScoresBySymbol(map);
    } catch {
      /* swallow — table falls back to "—" for affected rows */
    }
  }, []);

  useEffect(() => {
    if (!items?.length) return;
    hydrateScores();
  }, [items, hydrateScores]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Phase 3 — poll job-progress status for long-running admin jobs.
  // Two independent intervals: 5s for fundamentals, 3s for scores.
  // Keep polling continuously while the page is mounted. Previously the
  // pollers auto-stopped 10s after seeing a non-running status, which
  // meant that if the admin page sat idle and then the user clicked a
  // "Refresh" button, the progress card never appeared because nothing
  // was listening for updates anymore. A 3-5s poll is cheap — just let
  // it run for the whole session.
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const data = await apiGet("/admin/us-fundamentals/refresh-status");
        if (!cancelled) setFundProgress(data);
      } catch {
        /* ignore transient network/auth errors — next tick retries */
      }
    };
    poll();
    const timer = setInterval(poll, 5000);
    return () => { cancelled = true; clearInterval(timer); };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const data = await apiGet("/admin/us-scores/recompute-status");
        if (!cancelled) setScoreProgress(data);
      } catch {
        /* ignore transient network/auth errors — next tick retries */
      }
    };
    poll();
    const timer = setInterval(poll, 3000);
    return () => { cancelled = true; clearInterval(timer); };
  }, []);

  // Auto-refresh the table when a score recompute or fundamentals refresh
  // transitions from "running" → "completed". Without this the admin had
  // to manually reload the page to see newly-computed scores show up in
  // the Tickers list.
  const prevScoreStatus = useRef(null);
  useEffect(() => {
    const status = scoreProgress?.status;
    if (prevScoreStatus.current === "running" && status === "completed") {
      hydrateScores();
      // Also refresh the headline counters (SCORED tile, signal distribution)
      loadAll();
    }
    prevScoreStatus.current = status;
  }, [scoreProgress, hydrateScores, loadAll]);

  const prevFundStatus = useRef(null);
  useEffect(() => {
    const status = fundProgress?.status;
    if (prevFundStatus.current === "running" && status === "completed") {
      // Fundamentals refresh alone doesn't change scores, but it does
      // update the FUNDAMENTALS STORED counter + last-sync timestamps.
      loadAll();
    }
    prevFundStatus.current = status;
  }, [fundProgress, loadAll]);

  const filtered = useMemo(() => {
    if (!search) return items;
    const q = search.trim().toLowerCase();
    return items.filter(
      (r) => r.symbol?.toLowerCase().includes(q) || r.company_name?.toLowerCase().includes(q) || r.sector?.toLowerCase().includes(q)
    );
  }, [items, search]);

  // ----- Action handlers -----
  const wrapAction = async (label, fn) => {
    setLoadingAction(true);
    try { await fn(); } catch (e) {
      toast.error(`${label} failed`, { description: e?.response?.data?.detail || e.message });
    } finally { setLoadingAction(false); }
  };

  const onSeedFromCsv = () => wrapAction("Seed", async () => {
    const res = await apiPost("/admin/us-universe/seed-from-csv");
    toast.success("S&P 500 universe seeded", { description: `Inserted ${res.inserted}, updated ${res.updated}` });
    await loadAll();
  });

  const onRefreshNow = () => wrapAction("Price refresh", async () => {
    await apiPost("/admin/us-universe/refresh-now?period=10d");
    toast.success("Price refresh queued", { description: "Running in the background. Refresh in a minute." });
  });

  const onBackfill = () => wrapAction("Backfill", async () => {
    await apiPost("/admin/us-universe/backfill?years=2&only_missing=true");
    toast.success("2-year backfill queued", { description: "≈12 minutes for the full universe." });
  });

  const onRefreshFundamentals = (force = false) => wrapAction("Fundamentals refresh", async () => {
    const qs = force ? "?force=true" : "";
    await apiPost(`/admin/us-fundamentals/refresh${qs}`);
    if (force) {
      toast.success("Force refresh queued", {
        description: "All 509 stocks will be re-fetched (~13 min). Watch the progress card below.",
      });
    } else {
      toast.success("Smart refresh queued", {
        description: "Stocks refreshed within the last 7 days will be skipped. If everything is fresh, this finishes in 0s.",
      });
    }
    // Immediate status poll so the progress card appears without waiting
    // for the next 5s interval.
    apiGet("/admin/us-fundamentals/refresh-status").then(setFundProgress).catch(() => {});
  });

  const onRecomputeSectors = () => wrapAction("Sector medians", async () => {
    const res = await apiPost("/admin/us-sector-medians/recompute");
    const filled = Object.values(res.sectors || {}).filter((s) => s.constituents > 0).length;
    toast.success(`${filled} sectors recomputed`);
    await loadAll();
  });

  const onRecomputeScores = () => wrapAction("Score recompute", async () => {
    await apiPost("/admin/us-scores/recompute");
    toast.success("Score recompute queued", { description: "Running across all 509 stocks (~1–2 min). Progress card below." });
    // Immediate status poll so the progress card appears without waiting
    // for the next 3s interval.
    apiGet("/admin/us-scores/recompute-status").then(setScoreProgress).catch(() => {});
  });

  const onRecomputeOne = (symbol) => wrapAction("Recompute", async () => {
    const res = await apiPost(`/admin/us-stocks/${encodeURIComponent(symbol)}/recompute`);
    toast.success(`${symbol}: ${res.signal} (${res.composite_score?.toFixed(1)})`, { description: `Mode: ${res.growth_or_value}` });
    setScoresBySymbol((prev) => ({ ...prev, [symbol]: res }));
  });

  const [addBusy, setAddBusy] = useState(false);
  const [addResult, setAddResult] = useState(null);

  const onAddTicker = async () => {
    const sym = (addSymbol || "").trim().toUpperCase();
    if (!sym) { toast.error("Symbol is required"); return; }
    setAddBusy(true);
    setAddResult(null);
    try {
      // enrich=true is the default — backend will synchronously fetch
      // fundamentals + 2y prices + composite score before returning.
      const res = await apiPost(
        `/admin/us-universe/add?enrich=true`,
        { symbol: sym },
      );
      if (res?.status === "already_active") {
        toast.info(`${sym} is already in the universe`);
        setAddSymbol("");
        setAddDialogOpen(false);
        await loadAll();
        return;
      }
      if (res?.status !== "added") {
        toast.warning(`${sym}: ${res?.status || "unknown response"}`);
        return;
      }

      // Successful add — keep dialog open briefly to show enriched result.
      setAddResult(res);
      const scoreOk = res?.score?.status === "ok";
      const desc = scoreOk
        ? `Score ${res.score.composite_score?.toFixed?.(1)} · ${res.score.signal} · ${res.score.valuation_status || ""}`
        : `Fundamentals: ${res?.fundamentals?.status || "?"} · Score: ${res?.score?.status || "?"}`;
      if (scoreOk) {
        toast.success(`${sym} added & ready`, { description: desc });
      } else {
        toast.warning(`${sym} added, partial enrichment`, { description: desc });
      }
      await loadAll();
    } catch (e) {
      toast.error(`Add failed: ${e?.message || "unknown error"}`);
    } finally {
      setAddBusy(false);
    }
  };

  const closeAddDialog = () => {
    setAddSymbol("");
    setAddResult(null);
    setAddDialogOpen(false);
  };

  const onRemoveTicker = (symbol) => wrapAction("Remove", async () => {
    // Defensive confirmation since the row dropdown removed the AlertDialog flow.
    // Soft-delete is reversible (re-seed restores the row + retains historical prices).
    if (typeof window !== "undefined") {
      const ok = window.confirm(
        `Remove ${symbol} from the active universe?\n\n` +
        `This is a soft-delete — historical prices are retained and you can ` +
        `re-add the ticker any time.`,
      );
      if (!ok) return;
    }
    await apiPost(`/admin/us-universe/remove/${encodeURIComponent(symbol)}?reason=admin_removed`);
    toast.success(`Removed ${symbol}`);
    await loadAll();
  });

  const onBackfillOne = (symbol) => wrapAction("Backfill", async () => {
    const res = await apiPost(`/admin/us-universe/backfill/${encodeURIComponent(symbol)}?years=2`);
    if (res?.status === "ok") {
      toast.success(`${symbol}: ${res.rows_written} rows`, { description: `${res.earliest} → ${res.latest}` });
    } else {
      toast.warning(`${symbol}: ${res?.reason || "no data"}`);
    }
  });

  // ----- Per-stock refresh actions (added in Phase 7+) -----

  const onRefreshFundamentalsOne = (symbol) => wrapAction("Fundamentals", async () => {
    const res = await apiPost(`/admin/us-fundamentals/refresh/${encodeURIComponent(symbol)}`);
    toast.success(`${symbol} fundamentals refreshed`, {
      description: `Source: ${res?.source || "?"} · PE ${res?.pe_ratio?.toFixed?.(1) ?? "—"} · ${res?.growth_or_value || ""}`,
    });
    await loadAll();
  });

  const onRefreshPricesOne = (symbol) => wrapAction("Prices", async () => {
    const res = await apiPost(
      `/admin/us-stocks/${encodeURIComponent(symbol)}/refresh-prices?years=2`,
    );
    if (res?.status === "ok") {
      toast.success(`${symbol}: ${res.rows_written} price rows`, {
        description: `${res.earliest} → ${res.latest}`,
      });
    } else {
      toast.warning(`${symbol}: ${res?.reason || "no data"}`);
    }
    await loadAll();
  });

  const onRefreshAllOne = (symbol) => wrapAction("Refresh All", async () => {
    const res = await apiPost(
      `/admin/us-stocks/${encodeURIComponent(symbol)}/refresh-all?years=2`,
    );
    const score = res?.score || {};
    const fund = res?.fundamentals || {};
    const overall = res?.overall_status === "ok" ? "success" : "warning";
    const desc = [
      `Fundamentals: ${fund.status}${fund.source ? ` (${fund.source})` : ""}`,
      `Prices: ${res?.prices?.status || "?"}`,
      `Score: ${score.status === "ok"
        ? `${score.composite_score?.toFixed?.(1)} · ${score.signal}`
        : score.status || "?"}`,
    ].join(" · ");
    if (overall === "success") {
      toast.success(`${symbol} refreshed end-to-end`, { description: desc });
    } else {
      toast.warning(`${symbol} refreshed with warnings`, { description: desc });
    }
    if (score.status === "ok") {
      setScoresBySymbol((prev) => ({
        ...prev,
        [symbol]: {
          ...(prev[symbol] || {}),
          composite_score: score.composite_score,
          signal: score.signal,
          valuation_status: score.valuation_status,
          growth_or_value: score.growth_or_value,
        },
      }));
    }
    await loadAll();
  });

  // ----- Render -----
  const sigDist = scoringStats?.signal_distribution || {};
  const scoredCount = scoringStats?.scored_count ?? 0;
  const fundCount = scoringStats?.fundamentals_count ?? 0;

  return (
    <div className="space-y-6" data-testid="admin-us-universe-page">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">US Stocks Universe</h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Manage tickers + run yfinance fundamentals and the 6-layer composite scoring engine.
            All prices and fundamentals are pulled via <span className="font-mono text-xs">yfinance</span> only.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={loadAll} disabled={loadingList} data-testid="admin-us-universe-refresh-list">
            <RefreshCcw className={`h-3.5 w-3.5 mr-1.5 ${loadingList ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Dialog open={addDialogOpen} onOpenChange={(o) => { if (!o) closeAddDialog(); else setAddDialogOpen(true); }}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="admin-us-universe-add-dialog-open"><Plus className="h-3.5 w-3.5 mr-1.5" /> Add Ticker</Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Add a US ticker</DialogTitle>
                <DialogDescription>
                  Non-S&amp;P 500 tickers (e.g. JOBY, PLTR, HRB). On add we synchronously fetch
                  fundamentals + 2-year price history + composite score so the ticker is usable
                  immediately.
                </DialogDescription>
              </DialogHeader>
              {!addResult ? (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="us-add-symbol">Symbol</Label>
                    <Input
                      id="us-add-symbol"
                      placeholder="HRB"
                      value={addSymbol}
                      onChange={(e) => setAddSymbol(e.target.value)}
                      data-testid="admin-us-universe-add-symbol-input"
                      autoFocus
                      disabled={addBusy}
                    />
                  </div>
                  {addBusy ? (
                    <div className="rounded-md border bg-muted/40 p-3 space-y-2" data-testid="admin-us-universe-add-progress">
                      <div className="flex items-center gap-2 text-sm">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>Validating, fetching fundamentals + 2y prices, computing score...</span>
                      </div>
                      <Progress value={undefined} className="h-1.5" />
                      <div className="text-[11px] text-muted-foreground">
                        This usually takes ~5–10 seconds.
                      </div>
                    </div>
                  ) : null}
                  <DialogFooter>
                    <Button variant="outline" onClick={closeAddDialog} disabled={addBusy}>
                      Cancel
                    </Button>
                    <Button
                      onClick={onAddTicker}
                      disabled={addBusy || !addSymbol.trim()}
                      data-testid="admin-us-universe-add-submit"
                    >
                      {addBusy ? (
                        <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Adding...</>
                      ) : (
                        <>Add &amp; enrich</>
                      )}
                    </Button>
                  </DialogFooter>
                </>
              ) : (
                <div className="space-y-3" data-testid="admin-us-universe-add-result">
                  <div className="rounded-md border bg-emerald-500/5 border-emerald-500/30 p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                      <div className="font-semibold text-sm">{addResult.symbol} added successfully</div>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {addResult.company?.company_name || addResult.company_name || "—"}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="rounded-md border p-2">
                      <div className="text-[10px] uppercase text-muted-foreground">Fundamentals</div>
                      <div className="font-medium tabular-nums">
                        {addResult.fundamentals?.status === "ok"
                          ? `OK · ${addResult.fundamentals.source || "?"}`
                          : (addResult.fundamentals?.status || "—")}
                      </div>
                    </div>
                    <div className="rounded-md border p-2">
                      <div className="text-[10px] uppercase text-muted-foreground">Price rows</div>
                      <div className="font-medium tabular-nums">
                        {addResult.prices?.rows_written ?? "—"}
                      </div>
                    </div>
                    {addResult.score?.status === "ok" ? (
                      <>
                        <div className="rounded-md border p-2">
                          <div className="text-[10px] uppercase text-muted-foreground">Composite</div>
                          <div className="font-semibold tabular-nums text-base">
                            {addResult.score.composite_score?.toFixed?.(1)} <span className="text-xs text-muted-foreground">/100</span>
                          </div>
                        </div>
                        <div className="rounded-md border p-2">
                          <div className="text-[10px] uppercase text-muted-foreground">Signal</div>
                          <div className="font-semibold">{addResult.score.signal}</div>
                        </div>
                        <div className="rounded-md border p-2">
                          <div className="text-[10px] uppercase text-muted-foreground">Valuation</div>
                          <div className="font-medium">{addResult.score.valuation_status || "—"}</div>
                        </div>
                        <div className="rounded-md border p-2">
                          <div className="text-[10px] uppercase text-muted-foreground">Target</div>
                          <div className="font-medium tabular-nums">
                            ${addResult.score.base_target_price?.toFixed?.(2) ?? "—"}
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="col-span-2 rounded-md border border-amber-500/30 bg-amber-500/5 p-2 text-amber-700 dark:text-amber-400">
                        Score not computed — {addResult.score?.reason || "missing data"}
                      </div>
                    )}
                  </div>
                  <DialogFooter>
                    <Button onClick={closeAddDialog} data-testid="admin-us-universe-add-result-close">
                      Done
                    </Button>
                  </DialogFooter>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Phase 1 stats — universe + prices */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <StatTile icon={CheckCircle2} label="Active tickers" value={formatNumber(stats?.active_tickers)} hint={`Cap: ${stats?.hard_cap || 600}`} testId="us-universe-stat-active" />
        <StatTile icon={Globe2} label="S&P 500" value={formatNumber(stats?.sp500_count)} hint={`Custom: ${formatNumber(stats?.custom_count) || 0}`} testId="us-universe-stat-sp500" />
        <StatTile icon={Database} label="Price rows" value={formatNumber(stats?.total_price_rows)} hint={stats?.latest_price_date ? `Latest: ${stats.latest_price_date}` : "no prices yet"} testId="us-universe-stat-price-rows" />
        <StatTile icon={Clock} label="Last action" value={stats?.last_audit_event?.action ? stats.last_audit_event.action.replace(/_/g, " ") : "—"} hint={stats?.last_audit_event?.created_at ? formatDateTime(stats.last_audit_event.created_at) : ""} testId="us-universe-stat-last-action" />
      </div>

      {/* Phase 1 toolbar */}
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-sm flex items-center gap-2"><Database className="h-4 w-4" /> Universe &amp; Prices (Phase 1)</CardTitle></CardHeader>
        <CardContent className="flex flex-wrap gap-2 pt-0">
          <Button variant="outline" size="sm" onClick={onSeedFromCsv} disabled={loadingAction} data-testid="admin-us-universe-seed-csv">
            <Database className="h-3.5 w-3.5 mr-1.5" /> Re-seed S&amp;P 500
          </Button>
          <Button variant="outline" size="sm" onClick={onRefreshNow} disabled={loadingAction} data-testid="admin-us-universe-refresh-prices">
            <RefreshCcw className="h-3.5 w-3.5 mr-1.5" /> Refresh Prices Now
          </Button>
          <Button variant="outline" size="sm" onClick={onBackfill} disabled={loadingAction} data-testid="admin-us-universe-backfill">
            <Clock className="h-3.5 w-3.5 mr-1.5" /> Backfill 2 yrs
          </Button>
        </CardContent>
      </Card>

      {/* Phase 2 stats — scoring */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="us-scoring-stats-row">
        <StatTile icon={Sparkles} label="Fundamentals stored" value={formatNumber(fundCount)} hint={`of ${formatNumber(stats?.active_tickers)} active tickers`} testId="us-scoring-stat-fundamentals" accent={fundCount > 0 ? "value" : "warn"} />
        <StatTile icon={Calculator} label="Scored" value={formatNumber(scoredCount)} hint={`${formatNumber(scoringStats?.growth_count) || 0} growth · ${formatNumber(scoringStats?.value_count) || 0} value`} testId="us-scoring-stat-scored" accent={scoredCount > 0 ? "value" : "warn"} />
        <StatTile icon={BarChart3} label="Sector medians" value={formatNumber(scoringStats?.sector_medians_count)} hint="11 GICS sectors expected" testId="us-scoring-stat-sectors" accent={scoringStats?.sector_medians_count >= 11 ? "value" : "warn"} />
        <StatTile
          icon={TrendingUp}
          label="Signal distribution"
          value={scoredCount > 0 ? `${(sigDist.STRONG_BUY || 0) + (sigDist.BUY || 0)} BUY+` : "—"}
          hint={Object.entries(sigDist).map(([k, v]) => `${k.replace("_", " ")}: ${v}`).join(" · ")}
          testId="us-scoring-stat-signals"
        />
      </div>

      {/* Phase 2 toolbar */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2"><Calculator className="h-4 w-4" /> Fundamentals &amp; Scoring (Phase 2)</CardTitle>
          <CardDescription>
            Step 1 → Refresh fundamentals (yfinance) · Step 2 → Compute sector medians · Step 3 → Recompute composite scores.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2 pt-0">
          <div className="inline-flex rounded-md shadow-sm" data-testid="admin-us-fundamentals-refresh-group">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onRefreshFundamentals(false)}
              disabled={loadingAction}
              className="rounded-r-none border-r-0"
              data-testid="admin-us-fundamentals-refresh"
            >
              <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Refresh Fundamentals
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={loadingAction}
                  className="rounded-l-none px-2"
                  aria-label="Refresh fundamentals options"
                  data-testid="admin-us-fundamentals-refresh-menu"
                >
                  <ChevronDown className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72">
                <DropdownMenuLabel>Refresh mode</DropdownMenuLabel>
                <DropdownMenuItem
                  onClick={() => onRefreshFundamentals(false)}
                  data-testid="admin-us-fundamentals-refresh-smart"
                >
                  <Sparkles className="h-3.5 w-3.5 mr-2" />
                  <div className="flex flex-col">
                    <span>Smart refresh (default)</span>
                    <span className="text-xs text-muted-foreground">Skips stocks fresh within 7 days</span>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => onRefreshFundamentals(true)}
                  data-testid="admin-us-fundamentals-refresh-force"
                >
                  <Zap className="h-3.5 w-3.5 mr-2 text-amber-600" />
                  <div className="flex flex-col">
                    <span>Force refresh all 509</span>
                    <span className="text-xs text-muted-foreground">Ignores freshness window. ~13 min, hits yfinance hard.</span>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <Button variant="outline" size="sm" onClick={onRecomputeSectors} disabled={loadingAction} data-testid="admin-us-sector-medians-recompute">
            <BarChart3 className="h-3.5 w-3.5 mr-1.5" /> Recompute Sector Medians
          </Button>
          <Button variant="outline" size="sm" onClick={onRecomputeScores} disabled={loadingAction} data-testid="admin-us-scores-recompute">
            <Calculator className="h-3.5 w-3.5 mr-1.5" /> Recompute All Scores
          </Button>
        </CardContent>
      </Card>

      {/* Phase 3 — live progress for the long-running admin jobs.
          Only renders when a job has been triggered at least once. */}
      {(fundProgress?.started_at || scoreProgress?.started_at) ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="us-job-progress-row">
          <JobProgressCard title="Fundamentals refresh" progress={fundProgress} />
          <JobProgressCard title="Score recompute" progress={scoreProgress} />
        </div>
      ) : null}

      {/* Filters */}
      <Card>
        <CardContent className="pt-6 flex flex-col md:flex-row gap-3 md:items-end">
          <div className="flex-1 space-y-1.5">
            <Label htmlFor="us-search" className="text-xs">Search</Label>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input id="us-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filter by symbol, company, or sector…" className="pl-8" data-testid="admin-us-universe-search" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Membership</Label>
            <Select value={membershipFilter} onValueChange={setMembershipFilter}>
              <SelectTrigger className="w-44" data-testid="admin-us-universe-membership-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="S&P500">S&amp;P 500 only</SelectItem>
                <SelectItem value="custom">Custom only</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Visibility</Label>
            <Select value={activeOnly ? "active" : "all"} onValueChange={(v) => setActiveOnly(v === "active")}>
              <SelectTrigger className="w-44" data-testid="admin-us-universe-active-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active only</SelectItem>
                <SelectItem value="all">Include removed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Universe table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><Flag className="h-4 w-4" /> Tickers ({filtered.length})</CardTitle>
          <CardDescription>
            Score / Signal columns populate after Phase 2 fundamentals + score recompute. Click a row's "View" to see the 6-layer breakdown.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loadingList ? (
            <div className="p-6 space-y-2">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-9 w-full" />)}</div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground">
              No tickers match. <button className="underline" onClick={() => { setSearch(""); setMembershipFilter("all"); setActiveOnly(true); }}>Clear filters</button>.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Symbol</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Sector</TableHead>
                    <TableHead>Score</TableHead>
                    <TableHead>Signal</TableHead>
                    <TableHead>Mode</TableHead>
                    <TableHead>Membership</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((r) => {
                    const sc = scoresBySymbol[r.symbol];
                    return (
                      <TableRow key={r.symbol} data-testid={`us-universe-row-${r.symbol}`}>
                        <TableCell className="font-mono font-semibold">{r.symbol}</TableCell>
                        <TableCell className="max-w-xs truncate">{r.company_name || "—"}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{r.sector || "—"}</TableCell>
                        <TableCell className="tabular-nums">{sc?.composite_score != null ? sc.composite_score.toFixed(1) : "—"}</TableCell>
                        <TableCell><SignalBadge signal={sc?.signal} /></TableCell>
                        <TableCell>{sc?.growth_or_value ? <Badge variant="outline" className="text-xs">{sc.growth_or_value}</Badge> : <span className="text-xs text-muted-foreground">—</span>}</TableCell>
                        <TableCell>
                          {(r.index_membership || []).map((m) => (
                            <Badge key={m} variant={m === "S&P500" ? "default" : "secondary"} className="mr-1 text-[10px]">{m}</Badge>
                          ))}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button size="sm" variant="ghost" onClick={() => setScoreDialog({ open: true, symbol: r.symbol })} data-testid={`us-universe-view-${r.symbol}`} title="View score breakdown">
                              <Eye className="h-3 w-3 mr-1" /> View
                            </Button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  disabled={loadingAction}
                                  data-testid={`us-universe-actions-${r.symbol}`}
                                  title="Refresh / actions"
                                >
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-56">
                                <DropdownMenuLabel>{r.symbol} actions</DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() => onRefreshAllOne(r.symbol)}
                                  disabled={loadingAction}
                                  data-testid={`us-universe-refresh-all-${r.symbol}`}
                                >
                                  <Zap className="h-3.5 w-3.5 mr-2" /> Refresh All
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() => onRefreshFundamentalsOne(r.symbol)}
                                  disabled={loadingAction}
                                  data-testid={`us-universe-refresh-fund-${r.symbol}`}
                                >
                                  <Sparkles className="h-3.5 w-3.5 mr-2" /> Refresh Fundamentals
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => onRefreshPricesOne(r.symbol)}
                                  disabled={loadingAction}
                                  data-testid={`us-universe-refresh-prices-${r.symbol}`}
                                >
                                  <RefreshCcw className="h-3.5 w-3.5 mr-2" /> Refresh Prices
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => onRecomputeOne(r.symbol)}
                                  disabled={loadingAction}
                                  data-testid={`us-universe-recompute-${r.symbol}`}
                                >
                                  <Calculator className="h-3.5 w-3.5 mr-2" /> Recompute Score
                                </DropdownMenuItem>
                                {r.is_active ? (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      onClick={() => onRemoveTicker(r.symbol)}
                                      className="text-destructive focus:text-destructive"
                                      disabled={loadingAction}
                                      data-testid={`us-universe-remove-${r.symbol}`}
                                    >
                                      <Trash2 className="h-3.5 w-3.5 mr-2" /> Remove from universe
                                    </DropdownMenuItem>
                                  </>
                                ) : null}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Audit log */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><Clock className="h-4 w-4" /> Recent activity</CardTitle>
          <CardDescription>Last 20 admin actions on the universe.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {auditLog.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground text-center">No activity yet.</div>
          ) : (
            <ul className="divide-y" data-testid="admin-us-universe-audit-list">
              {auditLog.map((row, i) => (
                <li key={i} className="px-4 py-2.5 text-sm flex items-center gap-3">
                  <Badge variant="outline" className="font-mono">{row.action}</Badge>
                  <span className="font-mono text-xs">{row.symbol || "—"}</span>
                  <span className="text-xs text-muted-foreground flex-1 truncate">
                    {row.details && Object.keys(row.details).length > 0 ? JSON.stringify(row.details) : ""}
                  </span>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">{formatDateTime(row.created_at)}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <ScoreBreakdownDialog
        open={scoreDialog.open}
        onOpenChange={(o) => setScoreDialog((s) => ({ ...s, open: o }))}
        symbol={scoreDialog.symbol}
      />
    </div>
  );
};

export default AdminUsUniversePage;
